import { Maybe, UserId } from '@csp/csp-common-model';
import { StorageService } from '@csp/web-storage';

import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { AnalyticsClient } from '../model/AnalyticsClient';
import { EventProperties } from '../model/EventProperties';

type In = {
  analyticsClient: AnalyticsClient;
  userId: Maybe<string>;
  browserTabId: Maybe<string>;
  isEnabled: boolean;
  identifyProperties?: EventProperties;
};

type Out = {
  hasIdentifiedOnce: boolean;
};

type IdentifyParams = [UserId, EventProperties];

const STORAGE_KEY_PREVIOUS_IDENTIFY_PARAMS = 'useIdentifyUser__previousIdentifyParams__';
const createStorageKey = (browserTabId: string): string => `${STORAGE_KEY_PREVIOUS_IDENTIFY_PARAMS}${browserTabId}`;
const getFromSessionStorage = <T>(key: string): Maybe<T> => {
  const serializedPreviousIdenfityParams = StorageService.sessionStorage.getItem(key);

  if (!serializedPreviousIdenfityParams) {
    return undefined;
  }

  return JSON.parse(serializedPreviousIdenfityParams) as T;
};

// Identifies the user if:
// 1) We have not identified the user for this browser tab / session or
// 2) if the user params have been updated.
export const useIdentifyUser = ({
  analyticsClient,
  userId,
  isEnabled,
  browserTabId,
  identifyProperties = {},
}: In): Out => {
  const [hasIdentifiedOnce, setHasIdentifiedOnce] = useState(false);

  useEffect(() => {
    if (!userId || !isEnabled || !browserTabId) {
      if (browserTabId) {
        // Reset stored params so that we identify again if for example
        // the user is withdrawing and then re-accepting the analytics consent.
        StorageService.sessionStorage.setItem(createStorageKey(browserTabId), '');
      }
      return;
    }

    const identifyParams: IdentifyParams = [userId, identifyProperties];

    const storageKey = createStorageKey(browserTabId);
    const previousIdentifyParams = getFromSessionStorage<IdentifyParams>(storageKey);

    if (previousIdentifyParams === undefined || !isEqual(previousIdentifyParams, identifyParams)) {
      analyticsClient.identify(userId, { properties: identifyProperties });
      StorageService.sessionStorage.setItem(storageKey, JSON.stringify(identifyParams));
    }

    setHasIdentifiedOnce(true);
  }, [userId, identifyProperties, analyticsClient, isEnabled, browserTabId]);

  return { hasIdentifiedOnce };
};
